import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import AdministrativeDivisionSelector from 'containers/check-in/renew-member/AdministrativeDivisionSelector';
import { PremiumDetailsCard, PremiumSelector } from 'containers/check-in/renew-member/PremiaSelector';
import { useCreateHouseholdEnrollmentRecordMutation, useCreateMemberEnrollmentRecordMutation, useCreateMembershipPaymentMutation, useUpdateMemberMutation } from 'store/api/baseApiSlice';
import { CenteredModal, ConfirmDialog, isEligibleToRegister } from './member-renew-container';
import ErrorAlert, { SuccessAlert } from 'containers/member-import/ErrorAlert';
import { DatePicker } from 'components/inputs';
import { formatDate } from 'lib/formatters/date';
import { isEmpty, isNull } from 'lodash';
import { snakeCaseObject } from 'lib/utils';
import { addToast } from 'store/toasts/toasts-actions';

// EnrollmentTypeSelector Component
const EnrollmentTypeSelector = ({ enrollmentType, handleEnrollmentTypeChange }) => (
  <div className="tw-mt-2 tw-mb-2">
    <span className="tw-block tw-text-gray-800 tw-font-semibold tw-text-sm tw-mb-2">Enrollment Type</span>
    <div className="tw-grid tw-grid-cols-2 tw-gap-2">
      <EnrollmentTypeOption
        id="indigent-radio"
        label="Indigent"
        value="Indigent"
        enrollmentType={enrollmentType}
        onChange={handleEnrollmentTypeChange}
      />
      <EnrollmentTypeOption
        id="paying-radio"
        label="Paying"
        value="Paying"
        enrollmentType={enrollmentType}
        onChange={handleEnrollmentTypeChange}
      />
    </div>
  </div>
);

// EnrollmentTypeOption Component
const EnrollmentTypeOption = ({ id, label, value, enrollmentType, onChange }) => {
  const isSelected = enrollmentType === value;
  return (
    <div 
      className={`tw-flex tw-items-center tw-px-3 tw-py-2 tw-border-2 tw-rounded-lg tw-cursor-pointer tw-transition-all tw-duration-200 ${
        isSelected 
          ? 'tw-border-blue-500 tw-bg-blue-50 tw-shadow-md' 
          : 'tw-border-gray-200 hover:tw-border-gray-300 hover:tw-bg-gray-50'
      }`}
      onClick={() => onChange(value)}
    >
      <input
        id={id}
        type="radio"
        value={value}
        name="enrollmentType"
        checked={isSelected}
        onChange={() => onChange(value)}
        className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-white tw-border-gray-300 tw-focus:ring-blue-500 tw-focus:ring-2 tw-cursor-pointer"
      />
      <label
        htmlFor={id}
        className={`tw-w-full tw-ml-2 tw-text-sm tw-font-semibold tw-cursor-pointer ${
          isSelected ? 'tw-text-blue-700' : 'tw-text-gray-700'
        }`}
      >
        {label}
      </label>
    </div>
  );
};

const PaymentDetailsForm = ({
  paymentDate,
  receiptNumber,
  otherPayment,
  previouslyUnpaidAmount,
  setPaymentDate,
  setReceiptNumber,
  setOtherPayment,
  setPreviouslyUnpaidAmount
}) => (
  <div className="tw-mt-2 tw-mb-2 tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200">
    <h3 className="tw-text-gray-800 tw-font-semibold tw-text-sm tw-mb-2">Payment Details</h3>
    <div className="tw-grid tw-grid-cols-2 tw-gap-2">
      <div className="tw-mb-2">
        <label className="tw-block tw-text-gray-700 tw-font-medium tw-text-xs tw-mb-1">
          Payment Date
        </label>
        <DatePicker
          onChange={(gregorianDate, ethiopianDate) => setPaymentDate(ethiopianDate)}
          defaultDate={paymentDate}
          maxDate={formatDate(new Date())}
          overlayCalendar={true}
        />
      </div>
      <div className="tw-mb-2 tw-mt-1">
        <label className="tw-block tw-text-gray-700 tw-font-medium tw-text-xs tw-mb-1">Receipt Number</label>
        <input
          type="number"
          value={receiptNumber}
          onChange={(e) => setReceiptNumber(e.target.value)}
          className="tw-w-full tw-h-[44px] tw-px-3 tw-border tw-border-gray-300 tw-rounded-lg tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-transition-all tw-duration-200 hover:tw-border-gray-400 tw-text-sm"
        />
      </div>
      <div className="tw-mb-2">
        <label className="tw-block tw-text-gray-700 tw-font-medium tw-text-xs tw-mb-1">Previously Unpaid Amount (ዉዝፍ)</label>
        <input
          type="number"
          value={previouslyUnpaidAmount}
          onChange={(e) => setPreviouslyUnpaidAmount(e.target.value)}
          className="tw-w-full tw-h-[44px] tw-px-3 tw-border tw-border-gray-300 tw-rounded-lg tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-transition-all tw-duration-200 hover:tw-border-gray-400 tw-text-sm"
        />
      </div>
      <div className="tw-mb-2">
        <label className="tw-block tw-text-gray-700 tw-font-medium tw-text-xs tw-mb-1">Other Payment</label>
        <input
          type="number"
          value={otherPayment}
          onChange={(e) => setOtherPayment(e.target.value)}
          className="tw-w-full tw-h-[44px] tw-px-3 tw-border tw-border-gray-300 tw-rounded-lg tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-transition-all tw-duration-200 hover:tw-border-gray-400 tw-text-sm"
        />
      </div>
    </div>
  </div>
);


// InputField Component
const InputField = ({ label, type, value, onChange }) => (
  <div className="tw-mb-2">
    <label className="tw-block tw-text-gray-700 tw-font-medium tw-text-xs tw-mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="tw-w-full tw-h-10 tw-px-3 tw-border tw-border-gray-300 tw-rounded-lg tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-transition-all tw-duration-200 hover:tw-border-gray-400 tw-text-sm"
    />
  </div>
);

// PaymentSummary Component with updated sections
const PaymentSummary = ({
  renewal,
  enrollmentType,
  selectedPremium,
  paymentDate,
  receiptNumber,
  otherPayment,
  numberOfQualifyingBeneficiaries,
  previouslyUnpaidAmount,
  payAdditionalPayment,
  additionalWifeFee = 0,
  selectedAdditionalWivesCount = 0,
}) => {
  const annualContributionFee = !payAdditionalPayment ? selectedPremium?.payment_types?.find(pt => pt.payment_name === 'annual_contribution_fee')?.amount || 0 : 0;
  const registrationFee = !renewal ? selectedPremium?.payment_types?.find(pt => pt.payment_name === 'registration_fee')?.amount || 0 : 0;
  const qualifyingBeneficiariesFee = selectedPremium?.payment_types?.find(pt => pt.payment_name === 'qualifying_beneficiaries_fee')?.amount || 0;
  
  // Total includes otherPayment and additionalWifeFee
  const totalAmount = (annualContributionFee + registrationFee + (qualifyingBeneficiariesFee * numberOfQualifyingBeneficiaries) + (otherPayment * 100) + (previouslyUnpaidAmount * 100) + additionalWifeFee) / 100;

  return (
    <div className="tw-mb-2">      
      {/* Monetary Values Section */}
      <div className="tw-mb-2">
        <span className="tw-block tw-text-gray-800 tw-font-semibold tw-text-sm tw-mb-1">Payment Summary</span>
        <div className="tw-bg-white tw-p-2 tw-rounded-lg tw-shadow-md tw-border tw-border-gray-200">
          <ul className="tw-space-y-1">
            {!payAdditionalPayment && <SummaryItem label="Annual Contribution Fee" value={formatCurrency(annualContributionFee)} />}
            {(renewal && !payAdditionalPayment) && <SummaryItem label="Registration Fee" value={formatCurrency(registrationFee)} />}
            <SummaryItem label="Registration Fee" value={formatCurrency(registrationFee)} />
            <SummaryItem label="Qualifying Beneficiaries Fee" value={formatCurrency(qualifyingBeneficiariesFee * numberOfQualifyingBeneficiaries)} />
            {additionalWifeFee > 0 && (
              <SummaryItem 
                label={`Additional Wife Fee (${selectedAdditionalWivesCount} ${selectedAdditionalWivesCount === 1 ? 'wife' : 'wives'})`} 
                value={formatCurrency(additionalWifeFee)} 
              />
            )}
            <SummaryItem label="Previously Unpaid Amount (ዉዝፍ)" value={formatCurrency(previouslyUnpaidAmount * 100)} />
            <SummaryItem label="Other Payment" value={formatCurrency(otherPayment * 100)} />
          </ul>
          {/* Total Summary Line */}
          <div className="tw-flex tw-justify-between tw-py-1 tw-mt-2 tw-pt-2 tw-border-t tw-border-gray-300 tw-font-bold tw-text-sm tw-text-blue-600">
            <span>Total Amount:</span>
            <span className="tw-text-blue-700">{formatCurrency(totalAmount * 100)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// SummaryItem Component
const SummaryItem = ({ label, value }) => (
  <li className="tw-flex tw-justify-between tw-items-center tw-py-0.5">
    <span className="tw-text-gray-600 tw-font-medium tw-text-xs">{label}:</span>
    <span className="tw-font-semibold tw-text-gray-800 tw-text-xs">{value}</span>
  </li>
);

// Helper function to format currency values
const formatCurrency = (amount) => {
  return `$${(amount / 100).toFixed(2)}`;
};


const SidePanel = ({
  dependents,
  setDependents,
  setRenewalProgress,
  administrativeDivisions,
  selectedGote,
  setSelectedGote,
  selectedPremium,
  setSelectedPremium,
  selectedEnrollmentPeriod,
  setSelectedEnrollmentPeriod,
  householdEnrollmentRecord,
  householdHead,
  refetch,
  memberEnrollmentRecords = [],
  selectedSecondWifeId,
  setSelectedSecondWifeId,
}) => {
  const [enrollmentType, setEnrollmentType] = useState(null);
  const [alert, setAlert] = useState({ message: null, type: null });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null, isLoading: false });
  const [paymentDate, setPaymentDate] = useState(null);
  const dispatch = useDispatch();
  const [receiptNumber, setReceiptNumber] = useState('');
  const [otherPayment, setOtherPayment] = useState(0);
  const [previouslyUnpaidAmount, setPreviouslyUnpaidAmount] = useState(0);
  const [createMemberEnrollmentRecord] = useCreateMemberEnrollmentRecordMutation();
  const [createHouseholdEnrollmentRecord] = useCreateHouseholdEnrollmentRecordMutation();
  const [createMembershipPayment] = useCreateMembershipPaymentMutation();
  const [numberOfQualifyingBeneficiaries, setNumberOfQualifyingBeneficiaries] = useState(0);
  const [updateMember] = useUpdateMemberMutation();
  
  const renewal = householdEnrollmentRecord !== null;
  
  // Function to detect wives
  const getWives = () => {
    return dependents.filter(dep => {
      const relationship = dep.relationshipToHead?.toLowerCase() || '';
      return relationship === 'wife';
    });
  };
  
  const wives = getWives();
  const hasMultipleWives = wives.length > 1;
  // Get all wives except the first one (additional wives)
  const additionalWives = hasMultipleWives ? wives.slice(1) : [];
  
  // Calculate additional wife fee: fee per additional wife * number of selected additional wives
  const additionalWifeFeePerPerson = selectedPremium
    ? selectedPremium?.payment_types?.find(pt => pt.payment_name === 'additional_wife_fee')?.amount || 0
    : 0;
  
  // Count how many additional wives are selected
  // If selectedSecondWifeId is an array, count its length; otherwise check if it's set
  const selectedAdditionalWivesCount = Array.isArray(selectedSecondWifeId) 
    ? selectedSecondWifeId.length 
    : (selectedSecondWifeId ? 1 : 0);
  
  const additionalWifeFee = additionalWifeFeePerPerson * selectedAdditionalWivesCount;
  const registerButtonText = householdEnrollmentRecord === null 
    ? 'Enroll Beneficiaries' 
    : householdHead?.active 
      ? 'Pay for Additional Beneficiary' 
      : 'Renew Beneficiaries';

  const countQualifyingBeneficiaries = (dependents) => dependents.filter(member => !isEligibleToRegister(snakeCaseObject(member)) && member.enabled && !member.active).length;

  const hasEligibleMembers = () => dependents.some(member => member.enabled && !member.active);

  const displayError = (message) => {
    setAlert({ message, type: 'alert' });
  };

  const showConfirmDialog = (message, onConfirm) => {
    setConfirmDialog({ isOpen: true, message, onConfirm, isLoading: false });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ isOpen: false, message: '', onConfirm: null, isLoading: false });
  };

  const setConfirmDialogLoading = (isLoading) => {
    setConfirmDialog(prev => ({ ...prev, isLoading }));
  };

  const handlePaymentCreation = async (paymentData) => {
    // Ensure premium_id is included in payment data
    const paymentDataWithPremium = {
      ...paymentData,
      premium_id: paymentData.premium_id || selectedPremium?.id,
    };
    return await createMembershipPayment(paymentDataWithPremium);
  };

  const handleMemberEnrollment = async (member) => {
    // Skip if member is already active or has active enrollment
    if (member?.active) {
      console.log(`Member ${member.id} is already active, skipping enrollment record creation`);
      return null;
    }

    // Check if member enrollment record already exists for current enrollment period
    if (selectedEnrollmentPeriod?.id && memberEnrollmentRecords?.length > 0) {
      const existingRecord = memberEnrollmentRecords.find(
        (mer) => mer.member_id === member.id && mer.enrollment_period_id === selectedEnrollmentPeriod.id
      );
      if (existingRecord) {
        console.log(`Member ${member.id} already has enrollment record for enrollment period ${selectedEnrollmentPeriod.id}`);
        return null;
      }
    }

    try {
      const memberEnrollmentData = {
        member_id: member.id,
        user_id: 456, // Replace with actual user ID
        enrollment_period_id: selectedEnrollmentPeriod.id,
        enrolled_at: new Date(),
      };
      return await createMemberEnrollmentRecord(memberEnrollmentData).unwrap();
    } catch (error) {
      // Handle duplicate key error - member already enrolled in this period
      const errorMessage = error?.data?.exception || error?.data?.error || error?.message || '';
      const isDuplicateError = error?.status === 500 && (
        errorMessage.includes('duplicate key value violates unique constraint') ||
        errorMessage.includes('index_mer_on_member_id_and_enrollment_period_id') ||
        errorMessage.includes('already exists')
      );
      
      if (isDuplicateError) {
        // Member is already enrolled, this is fine - just return
        console.log(`Member ${member.id} is already enrolled in enrollment period ${selectedEnrollmentPeriod.id}`);
        return null;
      }
      // Re-throw other errors
      throw error;
    }
  };

  const processDependents = async (dependents) => {
    for (const member of dependents) {
      // Only create member enrollment record if member doesn't have membership number and is not active
      if (!member?.membership_number && !member?.active) {
        try {
          await handleMemberEnrollment(member);
        } catch (error) {
          // Log error but continue processing other members
          console.error(`Failed to enroll member ${member.id}:`, error);
        }
      }
      if ((enrollmentType === 'Indigent' || !householdEnrollmentRecord?.paying) || (member.enabled && !member.active &&  (enrollmentType === 'Paying' || householdEnrollmentRecord?.paying))) {
        setRenewalProgress((prev) => ({ ...prev, [member.id]: 'loading' }));
        try {

          // If member is enabled and not active or enrollment type is Indigent, set archive fields to nil
          const formData = new FormData();
          formData.append('id', member.id);
          formData.append('archive_reason', '');
          formData.append('archived_at', '');
          const member = await updateMember(formData);
          setRenewalProgress((prev) => ({ ...prev, [member.id]: 'success' }));
          setDependents((prev) =>
            prev.map((dep) => (dep.id === member.id ? { ...dep, active: true } : dep))
          );
        } catch (error) {
          setRenewalProgress((prev) => ({ ...prev, [member.id]: 'error' }));
        }
      } else {
        if (!member.active) {
          const formData = new FormData();
        formData.append('id', member.id);
        formData.append('archive_reason', 'UNPAID');
        formData.append('archived_at', new Date());
        await updateMember(formData);
        }
      }
    }
  };
  const payForAdditionalBeneficiaries = async () => {
    if (enrollmentType === 'Paying' && !receiptNumber) {
      displayError('Please select the receipt number.');
      return;
    }
    if (enrollmentType === 'Paying' && !paymentDate) {
      displayError('Please select the payment date.');
      return;
    }
    
    showConfirmDialog('Are you sure you want to register all household members?', async () => {
      setConfirmDialogLoading(true);
      try {
        const membershipPaymentData = {
          premium_id: selectedPremium?.id,
          receipt_number: receiptNumber || '',
          payment_date: paymentDate || '',
          qualifying_beneficiaries_fee: (selectedPremium?.payment_types?.find(pt => pt.payment_name === 'qualifying_beneficiaries_fee')?.amount || 0) * numberOfQualifyingBeneficiaries,
          additional_wife_fee: additionalWifeFee,
          other_fee: otherPayment * 100,
          previously_unpaid_amount: previouslyUnpaidAmount * 100,
          household_enrollment_record_id: householdEnrollmentRecord.id,
        };

        await handlePaymentCreation(membershipPaymentData);
        await processDependents(dependents);
        closeConfirmDialog();
        dispatch(addToast({ message: 'Household members registered successfully.', type: 'success' }));
        refetch();
      } catch (error) {
        setConfirmDialogLoading(false);
        const errorMessage = error?.data?.message || error?.message || 'An error occurred while registering household members. Please try again.';
        displayError(errorMessage);
      }
    });
    return;
  };

  const renewHouseholdMembers = async () => {
    if (!hasEligibleMembers()) {
      displayError('No eligible members to register.');
      return;
    }
    if (householdEnrollmentRecord?.paying && registerButtonText === 'Pay for Additional Beneficiary') {
      payForAdditionalBeneficiaries();
      return;
    }

    if (!selectedGote && registerButtonText !== 'Pay for Additional Beneficiary') {
      displayError('Please select the Gote Division.');
      return;
    }

    if (!enrollmentType && registerButtonText !== 'Pay for Additional Beneficiary') {
      displayError('Please select the enrollment type.');
      return;
    }

    if (enrollmentType === 'Paying' && !receiptNumber) {
      displayError('Please select the receipt number.');
      return;
    }

    if (enrollmentType === 'Paying' && !paymentDate) {
      displayError('Please select the payment date.');
      return;
    }

    showConfirmDialog('Are you sure you want to register all household members?', async () => {
      setConfirmDialogLoading(true);
      try {
        let createdHouseholdEnrollmentRecord = null
        console.log(dependents[0].householdId);
        if (registerButtonText !== 'Pay for Additional Beneficiary') {
          const enrollmentRecordData = {
            household_id: dependents[0].householdId,
            user_id: 456,
            enrollment_period_id: selectedEnrollmentPeriod.id,
            administrative_division_id: selectedGote.value,
            enrolled_at: new Date(),
            paying: enrollmentType === 'Paying',
            renewal,
          };
    
          createdHouseholdEnrollmentRecord = await createHouseholdEnrollmentRecord(enrollmentRecordData).unwrap();
        }

        if (enrollmentType === 'Paying' || householdEnrollmentRecord?.paying) {
          const membershipPaymentData = {
            premium_id: selectedPremium?.id,
            receipt_number: receiptNumber || '',
            payment_date: paymentDate || '',
            annual_contribution_fee: selectedPremium?.payment_types?.find(pt => pt.payment_name === 'annual_contribution_fee')?.amount || 0,
            registration_fee: selectedPremium?.payment_types?.find(pt => pt.payment_name === 'registration_fee')?.amount || 0,
            qualifying_beneficiaries_fee: selectedPremium?.payment_types?.find(pt => pt.payment_name === 'qualifying_beneficiaries_fee')?.amount || 0,
            additional_wife_fee: additionalWifeFee,
            other_fee: otherPayment * 100,
            previously_unpaid_amount: previouslyUnpaidAmount * 100,
            household_enrollment_record_id: createdHouseholdEnrollmentRecord.id,
          };

          await handlePaymentCreation(membershipPaymentData);
        }

        await processDependents(dependents);
        closeConfirmDialog();
        const successMessage = renewal ? 'Household members renewed successfully.' : 'Household members enrolled successfully.';
        dispatch(addToast({ message: successMessage, type: 'success' }));
        refetch();
      } catch (error) {
        setConfirmDialogLoading(false);
        const errorMessage = error?.data?.message || error?.message || 'An error occurred while processing household members. Please try again.';
        displayError(errorMessage);
      }
    });
    return;
  };

  useEffect(() => {
    setNumberOfQualifyingBeneficiaries(countQualifyingBeneficiaries(dependents));
  }, [dependents]);

  const handleEnrollmentTypeChange = (type) => {
    setEnrollmentType(type);
    if (type !== 'Paying') {
      setPaymentDate(null);
      setReceiptNumber('');
      setOtherPayment(0);
      setPreviouslyUnpaidAmount(0);
      setSelectedPremium(null);
    }
  };

  const allDependentsActive = dependents.every((member) => member.active);

  if (allDependentsActive) {
  return (
    <div className="tw-w-full lg:tw-w-5/12 tw-pl-0 lg:tw-pl-4 tw-mt-6 lg:tw-mt-0">
      <div className="tw-flex tw-flex-col tw-bg-white tw-h-full tw-rounded-lg tw-p-3 tw-shadow-lg tw-border tw-border-gray-200">
      <div className="tw-bg-gradient-to-r tw-from-green-50 tw-to-emerald-50 tw-border-2 tw-border-green-400 tw-text-green-800 tw-px-6 tw-py-5 tw-rounded-lg tw-text-center tw-shadow-lg">
            {/* Check Icon */}
            <div className="tw-flex tw-justify-center tw-mb-3">
              <div className="tw-bg-green-500 tw-rounded-full tw-p-2 tw-shadow-md">
                <svg
                  className="tw-h-6 tw-w-6 tw-text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            {/* Success Message */}
            <strong className="tw-font-bold tw-text-lg tw-block tw-mb-1">Success! </strong>
            <span className="tw-block sm:tw-inline tw-text-green-700">All members enrolled/registered.</span>
          </div>
      </div>
    </div>
  );
}



  return (
    <div className="tw-w-full lg:tw-w-5/12 tw-pl-0 lg:tw-pl-4 tw-mt-6 lg:tw-mt-0">
      <div className="tw-flex tw-flex-col tw-bg-white tw-h-full tw-rounded-lg tw-p-3 tw-shadow-lg tw-border tw-border-gray-200">
        {registerButtonText !== 'Pay for Additional Beneficiary' && (
          <AdministrativeDivisionSelector
            data={administrativeDivisions}
            onGoteChange={setSelectedGote}
            selectedGote={selectedGote}
          />
        )}

        {alert.message && (
          <CenteredModal 
            onClose={() => setAlert({ message: null, type: null })}
            type={alert.type === 'alert' ? 'danger' : alert.type}
            title={alert.type === 'alert' ? 'Error' : alert.type === 'success' ? 'Success' : 'Information'}
          >
            {alert.type === 'alert' && <ErrorAlert errors={[alert.message]} />}
            {alert.type === 'success' && <SuccessAlert message={alert.message} />}
          </CenteredModal>
        )}

        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={closeConfirmDialog}
          onConfirm={confirmDialog.onConfirm || (() => {})}
          message={confirmDialog.message}
          title="Confirm Action"
          confirmText="Confirm"
          cancelText="Cancel"
          isLoading={confirmDialog.isLoading}
        />

        <PremiumSelector
          administrativeDivisions={administrativeDivisions}
          setSelectedPremium={setSelectedPremium}
          setSelectedEnrollmentPeriod={setSelectedEnrollmentPeriod}
        />

        {!householdHead?.active && (
          <EnrollmentTypeSelector
            enrollmentType={enrollmentType}
            handleEnrollmentTypeChange={handleEnrollmentTypeChange}
          />
        )}

        {registerButtonText === 'Pay for Additional Beneficiary' && (
          <>
            <PaymentDetailsForm
              paymentDate={paymentDate}
              receiptNumber={receiptNumber}
              otherPayment={otherPayment}
              previouslyUnpaidAmount={previouslyUnpaidAmount}
              setPaymentDate={(gregorianDate, ethiopianDate) => setPaymentDate(gregorianDate)}
              setReceiptNumber={setReceiptNumber}
              setOtherPayment={setOtherPayment}
              setPreviouslyUnpaidAmount={setPreviouslyUnpaidAmount}
            />
            <PaymentSummary
              payAdditionalPayment={registerButtonText === 'Pay for Additional Beneficiary'}
              renewal={renewal}
              enrollmentType={enrollmentType}
              selectedPremium={selectedPremium}
              paymentDate={paymentDate}
              receiptNumber={receiptNumber}
              otherPayment={otherPayment}
              previouslyUnpaidAmount={previouslyUnpaidAmount}
              numberOfQualifyingBeneficiaries={numberOfQualifyingBeneficiaries}
              additionalWifeFee={additionalWifeFee}
              selectedAdditionalWivesCount={selectedAdditionalWivesCount}
            />
          </>
        )}

        {enrollmentType === 'Paying' && (
          <>
            <PaymentDetailsForm
              paymentDate={paymentDate}
              receiptNumber={receiptNumber}
              otherPayment={otherPayment}
              previouslyUnpaidAmount={previouslyUnpaidAmount}
              setPaymentDate={(gregorianDate, ethiopianDate) => setPaymentDate(gregorianDate)}
              setReceiptNumber={setReceiptNumber}
              setOtherPayment={setOtherPayment}
              setPreviouslyUnpaidAmount={setPreviouslyUnpaidAmount}
            />
            <PaymentSummary
              payAdditionalPayment={registerButtonText === 'Pay for Additional Beneficiary'}
              renewal={renewal}
              enrollmentType={enrollmentType}
              selectedPremium={selectedPremium}
              paymentDate={paymentDate}
              receiptNumber={receiptNumber}
              otherPayment={otherPayment}
              previouslyUnpaidAmount={previouslyUnpaidAmount}
              numberOfQualifyingBeneficiaries={numberOfQualifyingBeneficiaries}
              additionalWifeFee={additionalWifeFee}
              selectedAdditionalWivesCount={selectedAdditionalWivesCount}
            />
          </>
        )}

        <div className="tw-mt-3">
          <button
            type="button"
            onClick={renewHouseholdMembers}
            className="tw-w-full tw-px-6 tw-py-3 tw-bg-gradient-to-r tw-from-green-500 tw-to-emerald-500 tw-text-white tw-font-semibold tw-rounded-lg tw-shadow-lg hover:tw-from-green-600 hover:tw-to-emerald-600 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-green-400 focus:tw-ring-offset-2 tw-transition-all tw-duration-200 tw-flex tw-items-center tw-justify-center tw-gap-2 hover:tw-shadow-xl active:tw-scale-95"
          >
            <svg className="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{registerButtonText}</span>
          </button>
        </div>
      </div>
    </div>
  );
};


export default SidePanel;
